/**
 * Qualitative evaluation tags (solutions, impact path, lenses).
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { EVALUATION_TAG_META } from '@kuutio/shared/evaluationTags.js';

const Wrap = styled.div`
  margin-top: ${(p) => (p.$noMargin ? 0 : '12px')};
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
`;

// layout="rows" — auto-expanded, pill-as-section-title with reasoning beside it.
const RowsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  &:last-child { border-bottom: none; }
`;

const ChipStatic = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  flex-shrink: 0;
  white-space: nowrap;
  min-width: 130px;
  justify-content: center;
`;

const RowReason = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(p) => p.$muted};
  padding-top: 4px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: ${(p) => (p.$compact ? '0.72rem' : '0.78rem')};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-family: inherit;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    filter: brightness(1.05);
  }
`;

const Reason = styled.div`
  font-size: ${(p) => (p.$compact ? '0.8rem' : '0.85rem')};
  line-height: 1.5;
  color: ${(p) => p.$muted};
  margin: 6px 0 0 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${(p) => p.$reasonBg};
  border-left: 3px solid ${(p) => p.$accent};
`;

const ConflictBox = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const ConflictText = styled.div`
  font-size: 0.85rem;
  line-height: 1.55;
  color: ${(p) => p.$fg};
`;

function chipColors(id, variant) {
  const meta = EVALUATION_TAG_META[id] || {
    color: '#6b7280',
    bgLight: '#f9fafb',
    borderLight: '#e5e7eb',
    darkBg: 'rgba(255,255,255,0.06)',
    darkBorder: 'rgba(255,255,255,0.12)',
  };
  if (variant === 'light') {
    return { bg: meta.bgLight, border: meta.borderLight, fg: meta.color };
  }
  return { bg: meta.darkBg, border: meta.darkBorder, fg: meta.color };
}

export default function EvaluationTagChips({
  tags = [],
  contradictsResearch = null,
  researchStance = null,
  variant = 'dark',
  compact = false,
  maxVisible = 8,
  noTopMargin = false,
  // 'chips' (default) — pills in a flex-wrap row, click-to-expand reasoning.
  // 'rows' — auto-expanded two-column layout: pill on the left (acts like a
  // section title), reasoning on the right. Used in the Arviointi section so
  // facilitators can read all rationales without clicking each tag.
  layout = 'chips',
}) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(null);

  const list = Array.isArray(tags) ? tags.slice(0, maxVisible) : [];
  const rest = Array.isArray(tags) ? Math.max(0, tags.length - maxVisible) : 0;

  const reasonMuted = variant === 'light' ? '#4b5563' : 'rgba(255,255,255,0.55)';
  const reasonBg = variant === 'light' ? '#f9fafb' : 'rgba(255,255,255,0.04)';
  const conflictBg = variant === 'light' ? '#fef2f2' : 'rgba(239, 68, 68, 0.1)';
  const conflictBorder = variant === 'light' ? '#fecaca' : 'rgba(239, 68, 68, 0.35)';
  const conflictFg = variant === 'light' ? '#991b1b' : 'rgba(254, 202, 202, 0.95)';
  const silentBg = variant === 'light' ? '#f3f4f6' : 'rgba(255,255,255,0.06)';
  const silentBorder = variant === 'light' ? '#e5e7eb' : 'rgba(255,255,255,0.15)';
  const silentFg = variant === 'light' ? '#4b5563' : 'rgba(255,255,255,0.7)';
  const supportedBg = variant === 'light' ? '#ecfdf5' : 'rgba(5, 150, 105, 0.12)';
  const supportedBorder = variant === 'light' ? '#a7f3d0' : 'rgba(5, 150, 105, 0.35)';
  const supportedFg = variant === 'light' ? '#065f46' : 'rgba(167, 243, 208, 0.95)';

  const stance = researchStance
    || (contradictsResearch?.reasoning ? 'contradicted' : null);

  if (!list.length && !contradictsResearch?.reasoning && !stance) return null;

  const stanceBox = () => {
    if (stance === 'contradicted' && contradictsResearch?.reasoning) {
      return (
        <ConflictBox $bg={conflictBg} $border={conflictBorder}>
          <AlertTriangle size={18} color={variant === 'light' ? '#dc2626' : '#f87171'} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: conflictFg, marginBottom: 4 }}>
              {t('evaluationTags.researchStance.contradicted')}
            </div>
            <ConflictText $fg={conflictFg}>{contradictsResearch.reasoning}</ConflictText>
          </div>
        </ConflictBox>
      );
    }
    if (stance === 'silent') {
      return (
        <ConflictBox $bg={silentBg} $border={silentBorder}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: silentFg, marginBottom: 2 }}>
              {t('evaluationTags.researchStance.silent')}
            </div>
            <ConflictText $fg={silentFg}>{t('evaluationTags.researchStance.silentHint')}</ConflictText>
          </div>
        </ConflictBox>
      );
    }
    if (stance === 'supported') {
      return (
        <ConflictBox $bg={supportedBg} $border={supportedBorder}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: supportedFg }}>
              {t('evaluationTags.researchStance.supported')}
            </div>
          </div>
        </ConflictBox>
      );
    }
    if (contradictsResearch?.reasoning) {
      return (
        <ConflictBox $bg={conflictBg} $border={conflictBorder}>
          <AlertTriangle size={18} color={variant === 'light' ? '#dc2626' : '#f87171'} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: conflictFg, marginBottom: 4 }}>
              {t('evaluationTags.researchConflictTitle')}
            </div>
            <ConflictText $fg={conflictFg}>{contradictsResearch.reasoning}</ConflictText>
          </div>
        </ConflictBox>
      );
    }
    return null;
  };

  // Rows layout — auto-expanded two-column rows.
  if (layout === 'rows') {
    return (
      <Wrap $noMargin={noTopMargin}>
        <RowsCol>
          {list.map((tag, idx) => {
            const id = tag?.id;
            const labelKey = `evaluationTags.${id}`;
            const label = t(labelKey);
            const displayLabel = label === labelKey ? id : label;
            const { bg, border, fg } = chipColors(id, variant);
            return (
              <RowItem key={`${id}-${idx}`}>
                <ChipStatic $bg={bg} $border={border} $fg={fg}>{displayLabel}</ChipStatic>
                {tag?.reasoning && <RowReason $muted={reasonMuted}>{tag.reasoning}</RowReason>}
              </RowItem>
            );
          })}
        </RowsCol>
        {stanceBox()}
      </Wrap>
    );
  }

  return (
    <Wrap $noMargin={noTopMargin}>
      <Row>
        {list.map((tag, idx) => {
          const id = tag?.id;
          const labelKey = `evaluationTags.${id}`;
          const label = t(labelKey);
          const displayLabel = label === labelKey ? id : label;
          const { bg, border, fg } = chipColors(id, variant);
          const isOpen = expandedId === id;
          return (
            // Stop bubbling so clicks on the chip or expanded reason don't fire
            // the parent card's onClick (e.g. SuggestionCard's toggleSelect in
            // AILensItemsModal — was selecting/deselecting the card every time
            // a user clicked an eval tag to expand its reasoning).
            <div key={`${id}-${idx}`} onClick={(e) => e.stopPropagation()}>
              <Chip
                type="button"
                $compact={compact}
                $bg={bg}
                $border={border}
                $fg={fg}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedId(isOpen ? null : id);
                }}
                aria-expanded={isOpen}
              >
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {displayLabel}
              </Chip>
              {isOpen && tag?.reasoning && (
                <Reason $compact={compact} $muted={reasonMuted} $reasonBg={reasonBg} $accent={fg}>
                  {tag.reasoning}
                </Reason>
              )}
            </div>
          );
        })}
        {rest > 0 && (
          <span
            style={{
              fontSize: compact ? '0.72rem' : '0.78rem',
              fontWeight: 700,
              padding: '5px 10px',
              borderRadius: 20,
              border: variant === 'light' ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.12)',
              background: variant === 'light' ? '#f3f4f6' : 'rgba(255,255,255,0.06)',
              color: variant === 'light' ? '#374151' : 'rgba(255,255,255,0.65)',
            }}
          >
            +{rest}
          </span>
        )}
      </Row>

      {stanceBox()}
    </Wrap>
  );
}
